import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Col, Form, Input, Row, Space, Spin } from "antd";
import { has } from "lodash-es";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useIntl } from "react-intl";

import { HallUpdated, UpdateHall } from "@/events";
import { useResetForm } from "@/hooks";
import { useHallCreateSchema, THallCreateSchema } from "@/schemas";
import { HallsStore, useGlobalStore } from "@/stores";
import CurrencySelector from "@/ui/_common_/currency-selector";
import ErrorsFormatter from "@/ui/_common_/errors-formatter";
import Password from "@/ui/_common_/password";
import { generatePassword } from "@/utilities";
import yup from "@/yup";

type TProps = {
  hallsStore: HallsStore;
};

function HallCreateShortForm({ hallsStore }: TProps) {
  const { hallCreateStore } = hallsStore;
  const { eventBusService, routerService } = useGlobalStore();
  const hallCreateSchema = useHallCreateSchema();

  const schema = useMemo(() => {
    if (hallCreateStore.parentCurrency === null) {
      return hallCreateSchema;
    }
    return hallCreateSchema.concat(
      yup.object({
        currency: yup.string().equals([hallCreateStore.parentCurrency]),
      }),
    );
  }, [hallCreateSchema, hallCreateStore.parentCurrency]);

  const resolver = yupResolver(schema);
  const {
    watch,
    control,
    formState: { errors },
    handleSubmit,
    reset,
    getValues,
    setValue,
  } = useForm<THallCreateSchema>({
    defaultValues: {
      currency: hallCreateStore.parentCurrency ?? undefined,
      autoPrintReceipts: false,
      isBalanceUnlimited: Boolean(
        hallCreateStore.userAgentAdminIsBalanceUnlimited,
      ),
      lobbySettings: {
        playersIdleTimeout: 0,
        isLogOutButtonEnabled: true,
      },
    },
    resolver,
  });

  const values = watch();

  useResetForm(hallCreateStore.createQuery, reset);

  useEffect(() => {
    const listener = (event: HallUpdated) => {
      const searchParams = new URLSearchParams(routerService.location.search);
      searchParams.set("sq", String(event.payload.hallId));

      routerService.push("/agents");
      routerService.push({
        pathname: "/shops",
        search: searchParams.toString(),
      });
    };

    eventBusService.subscribe(UpdateHall, listener);

    return () => {
      eventBusService.unsubscribe(UpdateHall, listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setValue("name", values.login);
  }, [getValues, setValue, values.login]);

  useEffect(() => {
    hallCreateStore.initializeParentAgentSelection();
    return () => hallCreateStore.dispose();
  }, [hallCreateStore]);

  const intl = useIntl();

  return (
    <Spin spinning={hallCreateStore.createQuery.isPending}>
      <Space direction="vertical">
        <ErrorsFormatter queries={[hallCreateStore.createQuery]} />
        <Form
          labelCol={{ span: 10 }}
          labelAlign="left"
          layout="horizontal"
          onFinish={handleSubmit(hallCreateStore.handleSubmit)}
        >
          <Form.Item
            label={intl.formatMessage({ defaultMessage: "Admin username" })}
            validateStatus={has(errors, "login") ? "error" : ""}
            help={errors.login?.message}
          >
            <Controller
              control={control}
              name="login"
              defaultValue=""
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder={intl.formatMessage({
                    defaultMessage: "Enter username",
                  })}
                />
              )}
            />
          </Form.Item>
          <Form.Item
            label={intl.formatMessage({ defaultMessage: "Admin password" })}
            validateStatus={has(errors, "password") ? "error" : ""}
            help={errors.password?.message}
          >
            <Controller
              control={control}
              name="password"
              defaultValue=""
              render={({ field }) => (
                <Password
                  {...field}
                  placeholder={intl.formatMessage({
                    defaultMessage: "Enter password",
                  })}
                  onCopy={() => {
                    const password = getValues().password ?? "";
                    if (password) {
                      navigator.clipboard.writeText(password);
                    }
                  }}
                  onGenerate={() => setValue("password", generatePassword())}
                />
              )}
            />
          </Form.Item>
          {!hallCreateStore.parentCurrency && (
            <Form.Item
              label={intl.formatMessage({
                defaultMessage: "Currency",
              })}
              validateStatus={has(errors, "currency") ? "error" : ""}
              help={errors.currency?.message}
            >
              <Controller
                control={control}
                name="currency"
                defaultValue=""
                render={({ field }) => (
                  <CurrencySelector
                    {...field}
                    value={field.value ? field.value : undefined}
                  />
                )}
              />
            </Form.Item>
          )}
          <Row justify="end" gutter={12}>
            <Col>
              <Button type="primary" htmlType="submit">
                {intl.formatMessage({ defaultMessage: "Create" })}
              </Button>
            </Col>
          </Row>
        </Form>
      </Space>
    </Spin>
  );
}

export default observer(HallCreateShortForm);
